import { useEffect, useState, useRef } from "react";
import styles from "./css/nameBox.module.css";
import {
  checkInviteCode,
  checkUserName,
  sendError,
  signInAccount,
} from "./EnclaveUtils/functions";
import { createAccount } from "./EnclaveUtils/functionsV3";
import { setWindowEnclave } from "./EnclaveUtils/enclaveWindowFunctions";
import { CircularProgress } from "@mui/material";
import { useEnclaveConnect } from "./context/EnclaveConnectProvider";
import isPWA from "../../utils/pwaUtils";
import toast from "react-hot-toast";
import EnclaveLogo from "../EnclaveLogo.png";

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

export default function NameBox(props) {
  const { setUserName, setWalletAddress, setUserData } = useEnclaveConnect();
  const bg = useRef(null);

  const [warningText, setWarningText] = useState("");
  const [inviteWarningText, setInviteWarningText] = useState("");
  const [btnText, setBtnText] = useState("Create");
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");

  const [appState, setAppState] = useState("noName");

  const [userExixts, setUserExists] = useState(false);

  const [inviteCode, setInviteCode] = useState("");

  const [isInviteCodeValid, setIsInviteCodeValid] = useState(false);

  const debouncedName = useDebounce(name, 500);
  const debouncedInviteCode = useDebounce(inviteCode, 500);
  const [step, setStep] = useState(0);

  useEffect(() => {
    bg.current.addEventListener("click", (e) => {
      if (e.target === bg.current) {
        props.setNameBox(false);
        props.setConnectionState("disconnected");
      }
    });
  }, []);

  const btnClicked = async (e) => {
    e.preventDefault();

    if (appState === "nameEntered") {
      if (userExixts) {
        setLoading(true);
        try {
          const res = await signInAccount(name);
          if (res.authenticated) {
            localStorage.setItem("userData", JSON.stringify(res));
            console.log(localStorage.getItem("userData"));
            setUserName(res.username);
            setWalletAddress(res.wallet.scw_address);
            setUserData(res);
            props.setConnectionState("connected");
            props.setNameBox(false);
            setWindowEnclave(res);
            toast.success("Signed in successfully", {
              style: {
                fontSize: "1rem",
                borderRadius: "50px",
                background: "var(--gray-800)",
                color: "#fff",
                border: "1px solid var(--gray-600)",
              },
            });
          } else {
            setWarningText("Sign in failed");
          }
        } catch (error) {
          console.error("E1: ", error);
          sendError({
            message: "SIGN IN FAIL",
            error: JSON.stringify(error),
          });
          toast.error("Sign in failed", {
            style: {
              fontSize: "1rem",
              borderRadius: "50px",
            },
          });
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(true);
        try {
          const res = await createAccount(name, inviteCode);
          if (res.wallet.scw_address) {
            localStorage.setItem("userData", JSON.stringify(res));
            setUserName(res.username);
            setWalletAddress(res.wallet.scw_address);
            setUserData(res);
            props.setConnectionState("connected");
            props.setNameBox(false);
            setWindowEnclave(res);
          } else {
            setWarningText("Account Creation failed");
          }
        } catch (error) {
          console.error("E2: ", error);
          sendError({
            message: "CREATE ACCOUNT FAIL",
            error: JSON.stringify(error),
          });
        } finally {
          setLoading(false);
        }
      }
    }

    if (appState === "accountCreated" || appState === "loggedIn") {
      setWarningText("Signing in...");
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
        setAppState("loggedIn");
        props.setNameBox(false);
      }, 1000);
    }
  };

  useEffect(() => {
    if (debouncedName.length > 0) {
      setAppState("nameEntered");
      setLoading(true);
      setWarningText("Searching for user...");
      (async () => {
        try {
          const res = await checkUserName(debouncedName);
          if (res.value === true) {
            setWarningText("User already exists");
            setBtnText("Sign In");
            setUserExists(true);
          } else {
            setWarningText("New User Detected");
            setBtnText("Continue");
            setUserExists(false);
          }
        } catch (error) {
          console.log(error);
        } finally {
          setLoading(false);
        }
      })();
    } else {
      setAppState("noName");
      setWarningText("");
      setUserExists(false);
    }
  }, [debouncedName]);

  useEffect(() => {
    if (debouncedInviteCode.length > 0) {
      (async () => {
        try {
          const res = await checkInviteCode(debouncedInviteCode);
          if (res?.valid !== true) {
            setInviteWarningText("");
            setIsInviteCodeValid(true);
          } else {
            setInviteWarningText("Invalid Invite Code");
            setIsInviteCodeValid(false);
          }
        } catch (error) {
          console.error("E3: ", error);
          setInviteWarningText("Invalid Invite Code");
          setIsInviteCodeValid(false);
        }
      })();
    } else {
      setIsInviteCodeValid(false);
    }
  }, [debouncedInviteCode]);

  const isButtonDisabled = () => {
    if (name.length <= 0) return true;
    return false;
  };

  return (
    <div ref={bg} className={styles.main}>
      <div
        className={`${styles.popUp} ${isPWA() ? styles.popUpOverrides : ""}`}
      >
        <div className={styles.logoContainer}>
          <img style={{ width: "35px" }} src={EnclaveLogo} alt="Connect" />
          {/* <LogIn /> */}
        </div>
        <h1>Sign In</h1>
        {/* {step === 0 && ( */}
        <>
          <input
            onChange={(e) => {
              // Check if input contains special characters
              const hasSpecialChars = /[^a-zA-Z0-9_]/.test(e.target.value);
              if (hasSpecialChars) {
                setWarningText(
                  "Username should not include special characters"
                );
              }
              // Only allow alphanumeric characters and underscores
              const sanitizedValue = e.target.value.replace(
                /[^a-zA-Z0-9_]/g,
                ""
              );
              setName(sanitizedValue.toLowerCase());
            }}
            value={name}
            type="text"
            placeholder="Enter your username here...."
          />
          {warningText.length > 0 && <p>{warningText}</p>}
        </>
        {/* )} */}

        {/* {warningText === "New User Detected" && step === 1 && (
          <>
            <input
              type="text"
              placeholder="Enter Invite Code here...."
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value)}
            />
            {inviteWarningText.length > 0 && <p>{inviteWarningText}</p>}
          </>
        )} */}
        <button
          disabled={isButtonDisabled()}
          onMouseDown={btnClicked}
          className={`${
            isButtonDisabled() && styles.disabled
          } ${"btn-primary"}`}
        >
          {loading ? (
            <CircularProgress
              size={20}
              color="inherit"
              style={{ color: "#111" }}
            />
          ) : (
            <span style={{ fontSize: "1.2rem" }}>
              {btnText.length > 0 && btnText}
            </span>
          )}
        </button>
      </div>
    </div>
  );
}
